import { qoursesApi } from '@/api/qourses.tsx'
import { pushModal } from '@/components/modals'
import useGetCourseMeetingsPublic from '@/hooks/public/useGetCourseMeetingsPublic.tsx'
import useGetLocationPublic from '@/hooks/public/useGetLocationPublic.tsx'
import useGetMediaImagePublic from '@/hooks/public/useGetMediaImagePublic.tsx'
import useGetMultipassProductsPublic from '@/hooks/public/useGetMultipassProductsPublic.tsx'
import useGetTermsOfServicePublic from '@/hooks/public/useGetTermsOfServicePublic.tsx'
import useGetUpcomingMeetingInstructorsPublic from '@/hooks/public/useGetUpcomingMeetingInstructorsPublic.tsx'
import { Skeleton } from '@/shadcn/components/ui/skeleton.tsx'
import { cn } from '@/shadcn/lib/utils.ts'
import { addressStringFromLocation, getImageUrl } from '@/utils.tsx'
import { useAuth0 } from '@auth0/auth0-react'
import Avatar from 'boring-avatars'
import { ChevronRight, MapPinHouse, MessageSquareX, Ticket, Trees } from 'lucide-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PublicCourseWithPriceDetailsEntity } from '../../../../../qourses-api-client'

export function CourseBrief(props: {
  course: PublicCourseWithPriceDetailsEntity
  organizationId: string
  onShowMultipasses: () => void
}) {
  const { isAuthenticated } = useAuth0()

  const [multipassBriefRendered, setMultipassBriefRendered] = useState(false)
  const [cancellationBriefRendered, setCancellationBriefRendered] = useState(false)
  const [locationBriefRendered, setLocationBriefRendered] = useState(false)

  const atLeastOneChildRendered =
    multipassBriefRendered || cancellationBriefRendered || locationBriefRendered

  return (
    <div>
      <div className="py-2">
        <InstructorBrief course={props.course} renderCallback={setMultipassBriefRendered} />
      </div>
      <div
        className={cn(
          'flex flex-col gap-y-6',
          atLeastOneChildRendered && 'border-y border-gray-300 py-8 mb-8 mt-4',
        )}
      >
        <MultipassBrief
          isAuthenticated={isAuthenticated}
          course={props.course}
          organizationId={props.organizationId}
          onShowMultipasses={props.onShowMultipasses}
          renderCallback={setMultipassBriefRendered}
        />
        <CancellationBrief course={props.course} renderCallback={setCancellationBriefRendered} />
        <LocationBrief course={props.course} renderCallback={setLocationBriefRendered} />
      </div>
    </div>
  )
}

function MultipassBrief(props: {
  isAuthenticated: boolean
  course: PublicCourseWithPriceDetailsEntity
  organizationId: string
  onShowMultipasses?: () => void
  renderCallback?: Dispatch<SetStateAction<boolean>>
}) {
  const { t: translate } = useTranslation()

  const {
    multipassProducts,
    isLoading: isLoadingMultipassProducts,
    isError: isErrorMultipassProducts,
  } = useGetMultipassProductsPublic(props.organizationId)

  const {
    meetings,
    isLoading: isLoadingMeetings,
    isError: isErrorMeetings,
  } = useGetCourseMeetingsPublic(props.course.id)

  const [multipassInfo, setMultipassInfo] = useState<{
    courseHasMultipassAvailable: boolean
    customerMultipassProductId: string | null
    overallUsagesRemaining: number | null
  }>({
    courseHasMultipassAvailable: false,
    customerMultipassProductId: null,
    overallUsagesRemaining: null,
  })

  useEffect(() => {
    // Don't proceed if we have no multipass products
    if (!multipassProducts || multipassProducts.length === 0) {
      setMultipassInfo({
        courseHasMultipassAvailable: false,
        customerMultipassProductId: null,
        overallUsagesRemaining: null,
      })
      return
    }

    const fetchMultipassInfo = async () => {
      try {
        // Fetch eligible courses for each multipass product
        const eligibleCoursesPromises = multipassProducts.map(async (multipassProduct) => {
          const eligibleCourses =
            await qoursesApi.multipassPublic.multipassPublicControllerGetMultipassProductEligibleCoursesV1(
              multipassProduct.id,
            )
          return eligibleCourses.some((c) => c.id === props.course.id)
        })

        // Wait for all promises to resolve
        const eligibleCoursesResults = await Promise.all(eligibleCoursesPromises)

        // Check if any multipass product has this course as eligible
        const courseHasMultipassAvailable = eligibleCoursesResults.some((result) => result)

        // Only fetch customer multipasses if user is authenticated
        let customerMultipassProductId = null

        let overallUsagesRemaining: number | null = null

        if (props.isAuthenticated) {
          try {
            // Get customer's multipasses for this specific course
            const customerCourseMutipasses =
              await qoursesApi.courseCustomer.courseCustomerControllerGetCourseMultipassesV1(
                props.course.id,
              )

            overallUsagesRemaining =
              customerCourseMutipasses.length > 0
                ? customerCourseMutipasses.reduce(
                    (sum, multipass) => sum + multipass.usagesRemaining,
                    0,
                  )
                : null

            customerMultipassProductId =
              customerCourseMutipasses.length > 0
                ? customerCourseMutipasses[0].multipassProductId
                : null
          } catch (error) {
            console.error('Error fetching customer multipasses:', error)
          }
        }

        setMultipassInfo({
          overallUsagesRemaining,
          courseHasMultipassAvailable,
          customerMultipassProductId,
        })
      } catch (error) {
        console.error('Error fetching course multipasses:', error)
        setMultipassInfo({
          courseHasMultipassAvailable: false,
          customerMultipassProductId: null,
          overallUsagesRemaining: null,
        })
      }
    }

    fetchMultipassInfo()
  }, [props.isAuthenticated, multipassProducts, props.course.id, props.organizationId])

  if (isLoadingMeetings || isLoadingMultipassProducts) {
    return <Skeleton className="w-full h-16" />
  }

  if (isErrorMeetings || isErrorMultipassProducts) {
    return null
  }

  // If no multipass is available for this course, return null
  if (!multipassInfo.courseHasMultipassAvailable) {
    return null
  }

  if (!meetings || meetings.length === 0) {
    return null
  }

  if (props.renderCallback) {
    props.renderCallback(true)
  }

  const hasCustomerMultipass =
    props.isAuthenticated && !!multipassInfo.customerMultipassProductId

  return (
    <div className="flex gap-x-4">
      <div className="flex shrink-0">
        <Ticket className="size-5" strokeWidth={2} />
      </div>
      <div className="flex flex-col gap-y-1">
        <h3 className="font-semibold text-base">
          {hasCustomerMultipass && multipassInfo.overallUsagesRemaining !== null
            ? translate(
                'pages.public.booking.courseBrief.multipass_available.title_with_remaining',
                { count: multipassInfo.overallUsagesRemaining },
              )
            : translate(
                hasCustomerMultipass
                  ? 'pages.public.booking.courseBrief.multipass_available.title'
                  : 'pages.public.booking.courseBrief.multipass_advertisement.title',
              )}
        </h3>
        <p className="text-muted-foreground text-sm">
          {translate(
            hasCustomerMultipass
              ? 'pages.public.booking.courseBrief.multipass_available.subtitle'
              : 'pages.public.booking.courseBrief.multipass_advertisement.subtitle',
          )}
        </p>
        {props.onShowMultipasses && (
          <button
            type="button"
            className="text-gray-600 hover:text-indigo-500 underline text-sm flex items-center cursor-pointer w-fit p-0 bg-transparent border-0"
            onClick={props.onShowMultipasses}
          >
            {translate(
              hasCustomerMultipass
                ? 'pages.public.booking.courseBrief.multipass_action_buy_another'
                : 'pages.public.booking.courseBrief.multipass_action',
            )}
            <ChevronRight className="size-4 ml-1" />
          </button>
        )}
      </div>
    </div>
  )
}

function CancellationBrief(props: {
  course: PublicCourseWithPriceDetailsEntity
  renderCallback?: Dispatch<SetStateAction<boolean>>
}) {
  const { termsOfService, isError, isLoading } = useGetTermsOfServicePublic(
    props.course.termsOfServiceId,
  )
  const { t: translate } = useTranslation()

  if (isLoading) {
    return <Skeleton className="w-full h-16" />
  }

  if (isError) {
    return null
  }

  if (!termsOfService.userCancellationEnabled || termsOfService.userCancellationDaysBefore < 0) {
    return null
  }

  props.renderCallback(true)

  return (
    <div className="flex gap-x-4">
      <div className="flex shrink-0">
        <MessageSquareX className="size-5" strokeWidth={2} />
      </div>
      <div className="flex flex-col gap-y-1">
        <h3 className="font-semibold text-base">
          {translate('pages.public.booking.courseBrief.cancellation.title', {
            days: termsOfService.userCancellationDaysBefore,
          })}
        </h3>
        <p className="text-muted-foreground text-sm">
          {translate('pages.public.booking.courseBrief.cancellation.subtitle', {
            days: termsOfService.userCancellationDaysBefore,
          })}
        </p>
        <div className="text-gray-600 hover:text-indigo-500 underline text-sm flex items-center">
          <Link target="_blank" to={'/book/legal/tos/' + termsOfService.id}>
            {translate('pages.public.booking.courseBrief.cancellation.tosLink')}
          </Link>
          <ChevronRight className="size-4 ml-1" />
        </div>
      </div>
    </div>
  )
}

function InstructorBrief(props: {
  course: PublicCourseWithPriceDetailsEntity
  renderCallback?: Dispatch<SetStateAction<boolean>>
}) {
  const { instructors, isLoading, isError } = useGetUpcomingMeetingInstructorsPublic(
    props.course.id,
  )

  const { t: translate } = useTranslation()

  function InstructorMediaImage(props: { mediaImageId: string }) {
    const { mediaImage, isLoading } = useGetMediaImagePublic(props.mediaImageId)

    if (isLoading) {
      return <Skeleton className="rounded-full size-[30px]" />
    }

    return (
      <img
        src={getImageUrl(mediaImage?.cloudflareImageId)}
        className="size-[40px] rounded-full object-cover"
      />
    )
  }

  if (isLoading) {
    return <Skeleton className="w-full h-16" />
  }

  if (isError) {
    return null
  }

  if (!instructors || instructors.length === 0) {
    return null
  }

  return (
    <div className="flex gap-x-5 items-center">
      <div className="flex flex-col shrink-0">
        {instructors.length > 0 && instructors[0] ? (
          instructors[0].avatarMediaImageId ? (
            <InstructorMediaImage mediaImageId={instructors[0].avatarMediaImageId} />
          ) : (
            <Avatar
              size={40}
              name={instructors[0].id}
              variant="beam"
              colors={['#fcafa5', '#f0d4dc', '#5f75da', '#b681e0', '#ee79ac']}
            />
          )
        ) : (
          <Avatar
            size={40}
            name="generic-instructor"
            variant="beam"
            colors={['#fcafa5', '#f0d4dc', '#5f75da', '#b681e0', '#ee79ac']}
          />
        )}
      </div>
      <div className="flex flex-col gap-y-1">
        <div>
          <h3 className="font-semibold text-base">
            {instructors.length === 1
              ? translate('pages.public.booking.courseBrief.instructors.title_one', {
                  name: instructors[0].firstName,
                })
              : translate('pages.public.booking.courseBrief.instructors.title_multiple', {
                  names: instructors
                    .map((instructor) => instructor.firstName)
                    .reduce((acc, name, index, array) => {
                      if (index === 0) return name
                      if (index === array.length - 1) return `${acc} und ${name}`
                      return `${acc}, ${name}`
                    }, ''),
                })}
          </h3>
          <p className="text-muted-foreground text-sm">
            {translate('pages.public.booking.courseBrief.instructors.subtitle')}
          </p>
        </div>
      </div>
    </div>
  )
}

function LocationBrief(props: {
  course: PublicCourseWithPriceDetailsEntity
  renderCallback?: Dispatch<SetStateAction<boolean>>
}) {
  const { location, isLoading, isError } = useGetLocationPublic(props.course.locationId)

  const { t: translate } = useTranslation()

  if (isLoading) {
    return <Skeleton className="w-full h-16" />
  }

  if (isError) {
    return null
  }

  if (location.addressLevel3 === null) {
    return null
  }

  props.renderCallback(true)

  return (
    <div className="flex gap-x-4">
      <div className="flex shrink-0">
        {location.outside ? (
          <Trees className="size-5" strokeWidth={2} />
        ) : (
          <MapPinHouse className="size-5" strokeWidth={2} />
        )}
      </div>
      <div className="flex flex-col gap-y-1">
        <h3 className="font-semibold text-base">
          {location.outside
            ? translate('pages.public.booking.courseBrief.location.title_outside', {
                location: location.addressLevel3,
              })
            : translate('pages.public.booking.courseBrief.location.title', {
                location: location.addressLevel3,
              })}
        </h3>
        <p className="text-muted-foreground text-sm">
          {location.outside
            ? translate('pages.public.booking.courseBrief.location.subtitle_outside', {
                address: addressStringFromLocation(location),
              })
            : translate('pages.public.booking.courseBrief.location.subtitle', {
                address: addressStringFromLocation(location),
              })}
        </p>
        <div
          className="text-gray-600 hover:text-indigo-500 underline text-sm flex items-center cursor-pointer"
          onClick={() => {
            pushModal('BookingPageCourseDetailsModal', {
              course: props.course,
            })
          }}
        >
          {translate('pages.public.booking.courseBrief.location.more')}
          <ChevronRight className="size-4 ml-1" />
        </div>
      </div>
    </div>
  )
}
